import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { Box, Grid, Button } from "@mui/material";
import { motion } from "framer-motion";
import {
  loadingText,
  getRandomLoadingAnimation,
  getLongRunningAnimation,
} from "config/constants";
import TypographyAtom from "components/TypographyAtom";
import CloseIcon from "@mui/icons-material/Close";

const LONG_LOADING_THRESHOLD = 30000; // 12 seconds threshold for "long loading" message
const VERY_LONG_LOADING_THRESHOLD = 60000; // 20 seconds threshold for specialized message
const MAX_LOG_TIME = 60; // Maximum seconds to consider for the logarithmic curve

const LoadingScreenContext = createContext();

export const LoadingScreenProvider = ({ children }) => {
  const [loadingData, setLoadingData] = useState();
  const [loadingVisible, setLoadingVisible] = useState(false);
  const [isLongLoading, setIsLongLoading] = useState(false);
  const [isVeryLongLoading, setIsVeryLongLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [cancelCallback, setCancelCallback] = useState(null);
  const [isFinishing, setIsFinishing] = useState(false);

  const loadingTimerRef = useRef(null);
  const veryLongLoadingTimerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const startTimeRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(loadingTimerRef.current);
      clearTimeout(veryLongLoadingTimerRef.current);
      clearInterval(progressIntervalRef.current);
    };
  }, []);

  // Initially set a random loading animation
  useEffect(() => {
    setLoadingData(getRandomLoadingAnimation());
  }, []);

  // Show the loading screen; accepts an optional cancel callback.
  const showLoadingScreen = useCallback((info = "", onCancel) => {
    setLoadingData(getRandomLoadingAnimation());
    setCancelCallback(() => onCancel);
    setLoadingVisible(true);
    setIsLongLoading(false);
    setIsVeryLongLoading(false);
    setIsFinishing(false);
    setElapsedTime(0);
    startTimeRef.current = Date.now();

    // Clear any existing timers/intervals.
    clearTimeout(loadingTimerRef.current);
    clearTimeout(veryLongLoadingTimerRef.current);
    clearInterval(progressIntervalRef.current);

    // Update elapsed time every second.
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsedTime(elapsed);
    }, 1000);

    // Show long-loading message after threshold.
    loadingTimerRef.current = setTimeout(() => {
      setIsLongLoading(true);
      setLoadingData(getLongRunningAnimation());
    }, LONG_LOADING_THRESHOLD);

    // Show very long loading message.
    veryLongLoadingTimerRef.current = setTimeout(() => {
      setIsVeryLongLoading(true);
    }, VERY_LONG_LOADING_THRESHOLD);
  }, []);

  // Hide the loading screen after finishing the progress bar.
  const hideLoadingScreen = useCallback(() => {
    // Trigger the finishing animation.
    setIsFinishing(true);

    // Stop updating elapsed time.
    clearInterval(progressIntervalRef.current);

    // Wait for the finishing animation before hiding the screen.
    setTimeout(() => {
      setLoadingVisible(false);
      setIsFinishing(false);
      setIsLongLoading(false);
      setIsVeryLongLoading(false);
      setCancelCallback(null);
    }, 600); // Adjust delay as needed for your finishing animation duration
  }, []);

  // Logarithmic progress: adds 1 to avoid log(0) and normalizes with MAX_LOG_TIME.
  const computeProgress = () => {
    const progress =
      100 * (Math.log(elapsedTime + 1) / Math.log(MAX_LOG_TIME + 1));
    return progress > 100 ? 100 : progress;
  };

  // Animation variants for the loading container.
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.4 } },
    exit: { opacity: 0, transition: { duration: 0.3 } },
  };

  const formatElapsedTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Cancel operation handler; invoke provided callback if available.
  const handleCancel = () => {
    if (cancelCallback && typeof cancelCallback === "function") {
      cancelCallback();
    }
    hideLoadingScreen();
  };

  return (
    <LoadingScreenContext.Provider
      value={{ showLoadingScreen, hideLoadingScreen }}
    >
      {children}
      {loadingVisible && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={containerVariants}
        >
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(10, 25, 41, 0.85)",
              display: "flex",
              zIndex: 9999999999,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                position: "relative",
                maxWidth: { xs: "90%", sm: "80%", md: "600px" },
                width: "100%",
                p: { xs: 3, sm: 4 },
                borderRadius: 2,
                backgroundColor: "rgba(16, 36, 57, 0.85)",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(99, 155, 225, 0.15)",
              }}
            >
              {/* Cancel button */}
              <Button
                onClick={handleCancel}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  minWidth: "auto",
                  p: "4px",
                  color: "rgba(255, 255, 255, 0.7)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "rgba(255, 255, 255, 0.9)",
                  },
                }}
                aria-label="Cancel loading"
              >
                <CloseIcon />
              </Button>

              <Grid container spacing={2} sx={{ alignItems: "center" }}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 2,
                      mb: 2,
                    }}
                  >
                    {loadingData?.icon}
                  </Box>

                  <TypographyAtom
                    variant="h4"
                    fontWeight={500}
                    sx={{
                      textAlign: "center",
                      textTransform: "none",
                      letterSpacing: 1,
                      color: "primary.alternateText",
                      mb: 1,
                    }}
                  >
                    {loadingData?.top_text}
                  </TypographyAtom>

                  <TypographyAtom
                    variant="body1"
                    fontWeight={400}
                    sx={{
                      textAlign: "center",
                      textTransform: "none",
                      color: "rgba(255, 255, 255, 0.8)",
                      mb: 3,
                    }}
                  >
                    {loadingData?.bottom_text}
                  </TypographyAtom>

                  {/* Logarithmic progress bar and elapsed time */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: "60%", sm: "50%", md: "40%" },
                        height: "8px",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "4px",
                        overflow: "hidden",
                        mb: 2,
                      }}
                    >
                      <motion.div
                        // If finishing, force width to 100%, otherwise compute progress dynamically.
                        style={{
                          height: "100%",
                          backgroundColor: isVeryLongLoading
                            ? "rgba(255, 193, 7, 0.8)"
                            : "rgba(99, 155, 225, 0.8)",
                          borderRadius: "4px",
                        }}
                        animate={{
                          width: isFinishing ? "100%" : `${computeProgress()}%`,
                        }}
                        transition={{ ease: "easeInOut", duration: 0.5 }}
                      />
                    </Box>
                    {/* <TypographyAtom
                      variant="caption"
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      {formatElapsedTime(elapsedTime)} elapsed
                    </TypographyAtom> */}
                  </Box>

                  {/* Long loading message and cancellation prompt */}
                  {isLongLoading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <TypographyAtom
                        variant="body2"
                        sx={{
                          textAlign: "center",
                          color: isVeryLongLoading
                            ? "warning.main"
                            : "info.light",
                          mt: 2,
                          mx: "auto",
                          maxWidth: "90%",
                        }}
                      >
                        {isVeryLongLoading
                          ? "This operation is taking longer than expected. Consider reducing the size of your selected area."
                          : "Consider reducing the size of your selected area."}
                      </TypographyAtom>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 2,
                          mt: 3,
                        }}
                      >
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={handleCancel}
                          sx={{
                            textTransform: "none",
                            borderColor: "rgba(239, 83, 80, 0.5)",
                            color: "rgba(239, 83, 80, 0.9)",
                            "&:hover": {
                              borderColor: "rgba(239, 83, 80, 0.9)",
                              backgroundColor: "rgba(239, 83, 80, 0.05)",
                            },
                          }}
                        >
                          Cancel operation
                        </Button>
                      </Box>
                    </motion.div>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        </motion.div>
      )}
    </LoadingScreenContext.Provider>
  );
};

export const useLoadingScreen = () => {
  const context = useContext(LoadingScreenContext);
  if (!context) {
    throw new Error(
      "useLoadingScreen must be used within a LoadingScreenProvider",
    );
  }
  return context;
};
